/**
 * Subjects dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SubjectsDataTable } from '@/components/subjects/SubjectsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateSubjectView } from '@/components/subjects/CreateSubjectView';
import { EditSubjectView } from '@/components/subjects/EditSubjectView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link SubjectsSection}. */
interface SubjectsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: subjects list. */
export const SubjectsSection: React.FC<SubjectsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('subjects');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editSubjectId, setEditSubjectId] = React.useState<string | null>(null);

  const handleCreateSubject = () => {
    setCreateFormOpen(true);
    setEditSubjectId(null);
    closeRow();
  };

  const handleEditSubject = (subjectId: string) => {
    setEditSubjectId(subjectId);
    setCreateFormOpen(false);
    closeRow();
  };

  const closeForm = () => {
    setCreateFormOpen(false);
    setEditSubjectId(null);
  };

  const handleSuccess = () => {
    closeForm();
    void queryClient.invalidateQueries({ queryKey: ['subjects'] });
    onSuccess?.();
  };

  return (
    <>
      <SubjectsDataTable
        showCreateButton={canCreate}
        onCreateSubject={handleCreateSubject}
        isSection={true}
        layoutWidth={layoutWidth}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditSubject={handleEditSubject}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={closeForm}>
        <CreateSubjectView onClose={closeForm} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editSubjectId !== null} onClose={closeForm}>
        {editSubjectId && (
          <EditSubjectView
            key={editSubjectId}
            subjectId={editSubjectId}
            onClose={closeForm}
            onSuccess={handleSuccess}
          />
        )}
      </SlidePanel>
    </>
  );
};
